/**
 * Data layer for share links (web/docs/sharing-plan.md §11, web/docs/data-layer.md).
 *
 * `POST /v1/shares` mints (or idempotently reuses) a code for a title or a
 * recommendation — Cognito-authenticated, like every other mutation.
 * `GET /v1/shares/:code` is the **public** resolver the `/s/:code` preview
 * screen and the show page's attribution chip both read: it must work with no
 * token, so it's called with `{ auth: false }` — the one deliberate exception
 * to "every request carries a bearer" in this codebase. `404` (unknown/revoked)
 * and `410` (target gone) are designed non-crash outcomes, not errors — they
 * resolve to a `SharePreviewResult` status instead of throwing.
 */

import { useRef } from 'react';
import { useMutation, useQuery, type UseMutationOptions } from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiGet, apiPost, ApiError } from '@/api/http';
import { translate } from '@/i18n/locale';

import type {
  ApiCreateShareRequest,
  ApiCreateShareResponse,
  ApiShareKind,
  ApiSharePreview,
} from '@shared/api-types';
import { mapShare, mapSharePreview } from '@shared/mappers';
import type { Share, SharePreviewResult } from '@shared/types';

export const sharePreviewKey = (code: string) => ['sharePreview', code] as const;

/**
 * `GET /v1/shares/:code` → the resolved preview, or `{ status: 'not_found' }`
 * / `{ status: 'gone' }` for the designed 404/410 outcomes. Public — no bearer
 * token is sent, and none is required.
 */
export async function fetchSharePreview(code: string): Promise<SharePreviewResult> {
  try {
    const res = await apiGet<ApiSharePreview>(`/v1/shares/${code}`, { auth: false });
    return mapSharePreview(res);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { status: 'not_found' };
    if (err instanceof ApiError && err.status === 410) return { status: 'gone' };
    throw err;
  }
}

/** The `/s/:code` preview screen's data, and the attribution chip's lookup. */
export function useSharePreview(code: string | undefined) {
  return useQuery({
    queryKey: sharePreviewKey(code ?? ''),
    queryFn: () => fetchSharePreview(code as string),
    enabled: Boolean(code),
    retry: false,
    staleTime: 60_000,
  });
}

export interface CreateShareInput {
  kind: ApiShareKind;
  titleType: 'show' | 'movie';
  tmdbId: string;
  /** Recommendation-only, ≤140 chars after trim (docs/sharing-plan.md §10.3). */
  note?: string;
}

/** `POST /v1/shares` — mints, or idempotently reuses, a share code. */
export async function createShare(input: CreateShareInput): Promise<Share> {
  const trimmed = input.note?.trim();
  const body: ApiCreateShareRequest = {
    kind: input.kind,
    target: { title_type: input.titleType, tmdb_id: input.tmdbId },
    ...(trimmed ? { note: trimmed } : {}),
  };
  const res = await apiPost<ApiCreateShareResponse>('/v1/shares', body);
  return mapShare(res);
}

/**
 * Share/Recommend mutation, with a toast + Retry on failure (the show
 * screen's other mutations follow the same shape — web/docs/data-layer.md
 * §3). The caller decides what a success does (copy, `navigator.share`, …)
 * via its own `onSuccess`.
 */
export function useCreateShare(
  options: Pick<UseMutationOptions<Share, Error, CreateShareInput>, 'onSuccess'> = {},
) {
  const mutateRef = useRef<(vars: CreateShareInput) => void>(() => {});
  const mutation = useMutation<Share, Error, CreateShareInput>({
    mutationFn: createShare,
    onSuccess: options.onSuccess,
    onError: (_err, vars) => {
      toast.error(translate('share.error.create'), {
        action: { label: translate('common.retry'), onClick: () => mutateRef.current(vars) },
      });
    },
  });
  mutateRef.current = mutation.mutate;
  return mutation;
}
